import { z } from "zod";

/**
 * Client for `/_builder/start`: the owner's one V1 site, created privately on first use. A card-site
 * owner keeps its reserved address; a new owner supplies one when starting.
 */
export const StartStatusSchema = z.object({
  siteId: z.string().nullable(),
  available: z.boolean(),
  cardSite: z.boolean().default(false),
  reservedSubdomain: z.string().nullable().default(null),
  /** The owner's card site has been published before, so Stax can build from it. */
  cardSitePublished: z.boolean().default(false),
});
export type StartStatus = z.infer<typeof StartStatusSchema>;
const StartedSchema = z.object({ siteId: z.string(), created: z.boolean(), migrating: z.boolean().default(false) });
const RefusedSchema = z.object({ error: z.literal("unavailable") });
const AddressProblemSchema = z.object({ problem: z.enum(["invalid", "reserved", "taken"]) });

/** Shared by the routes that read the start status, so each page asks at most once a minute. */
export const builderStartQueryKey = ["builder", "start"] as const;

type Fetch = typeof fetch;

/**
 * `null` means the builder isn't switched on here (404/503), so the caller keeps the legacy flow.
 * Other failures throw, so an outage is never mistaken for "no builder".
 */
export async function getBuilderStart(fetchImpl: Fetch = (...args) => fetch(...args)): Promise<StartStatus | null> {
  const response = await fetchImpl("/_builder/start", { credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json" } });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Builder start unavailable (${response.status})`);
  return StartStatusSchema.parse(await response.json());
}

/**
 * Creates (or returns) the owner's private site. Safe to repeat: the server returns the same site.
 * New owners provide a subdomain; card-site owners omit it and retain their reserved address.
 */
export async function startBuilderSite(
  subdomain?: string,
  fetchImpl: Fetch = (...args) => fetch(...args),
): Promise<{ siteId: string; created: boolean; migrating: boolean } | { refused: "unavailable" } | { problem: "invalid" | "reserved" | "taken" }> {
  const response = await fetchImpl("/_builder/start", {
    method: "POST", credentials: "same-origin", cache: "no-store",
    headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(subdomain ? { subdomain } : {}),
  });
  if (response.status === 503) {
    const refused = RefusedSchema.safeParse(await response.json().catch(() => null));
    if (refused.success) return { refused: refused.data.error };
  }
  if (response.status === 409) {
    const problem = AddressProblemSchema.safeParse(await response.json().catch(() => null));
    if (problem.success) return problem.data;
  }
  if (!response.ok) throw new Error(`Couldn't start a site (${response.status})`);
  return StartedSchema.parse(await response.json());
}

/**
 * Where `/builder` leads: the owner's site when they have one. Otherwise `/`, which already decides
 * between the legacy dashboard, onboarding and `/start`, so a legacy owner never gets a builder
 * site just by opening `/builder`.
 */
export function builderHome(status: StartStatus | null): { siteId: string } | null {
  return status?.siteId ? { siteId: status.siteId } : null;
}
